/**
 * Page Tracking Hook
 *
 * Automatically tracks page views when route changes.
 * Tracks to both Application Insights and Google Analytics.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';

/**
 * Get human-readable page name from pathname
 *
 * @param pathname - URL pathname
 * @returns Page name
 */
const getPageNameFromPath = (pathname: string): string => {
  // Remove leading slash and split by forward slashes
  const segments = pathname.replace(/^\//, '').split('/');

  // Handle specific routes
  if (segments[0] === '') return 'Dashboard';
  if (segments[0] === 'brands') {
    if (segments[1]) return 'Brand Details';
    return 'Brands List';
  }
  if (segments[0] === 'deals') {
    if (segments[1]) return 'Deal Details';
    return 'Deals List';
  }
  if (segments[0] === 'deliverables') return 'Deliverables';
  if (segments[0] === 'analytics') return 'Social Analytics';
  if (segments[0] === 'settings') return 'Settings';

  // Default: capitalize first segment
  return segments[0] ? segments[0].charAt(0).toUpperCase() + segments[0].slice(1) : 'Unknown Page';
};

/**
 * Hook to enable automatic page tracking
 *
 * Call this hook in your app root or layout component to track all route changes.
 * Tracks to both Application Insights and Google Analytics.
 *
 * @example
 * ```tsx
 * function App() {
 *   usePageTracking();
 *   return <Routes>...</Routes>;
 * }
 * ```
 */
export const usePageTracking = (): void => {
  const location = useLocation();
  const { analyticsService, googleAnalyticsService } = useAnalytics();

  useEffect(() => {
    const pageName = getPageNameFromPath(location.pathname);

    // Track to Application Insights
    analyticsService.trackPageView(pageName, undefined, {
      path: location.pathname,
      search: location.search,
    });

    // Track to Google Analytics
    googleAnalyticsService.trackPageView(location.pathname, pageName);
  }, [location, analyticsService, googleAnalyticsService]);
};
